import {
    Alert,
    Button,
    Divider,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { ChangeEvent, useContext, useState } from "react";
import { ErrorDetailContainer } from "../../../api/config";
import { EmployeeDto, EmployeeService, Gender } from "../../../api/psc";
import { DialogContainer, DialogHeader } from "../../../components/exports";
import ErrorParser from "../../../components/shared/ErrorParser";
import { DialogContext } from "../../../contexts/DialogueContext";

export default function UpsertEmployeeDialog(props: {
    employee?: EmployeeDto;
}) {
    const { closeDialog } = useContext(DialogContext);

    const [firstName, setFirstName] = useState(props.employee?.firstName ?? "");
    const [lastName, setLastName] = useState(props.employee?.lastName ?? "");
    const [mobileNumber, setMobileNumber] = useState(
        props.employee?.mobileNumber ?? ""
    );
    const [jobTitle, setJobTitle] = useState(props.employee?.jobTitle ?? "");
    const [email, setEmail] = useState(props.employee?.email ?? "");
    const [age, setAge] = useState<number | undefined>(props.employee?.age);
    const [gender, setGender] = useState<Gender | undefined>(
        props.employee?.gender
    );
    // PIN: Personal Identification Number
    const [pin, setPin] = useState<null | string>(null);
    const [fathersName, setFathersName] = useState<null | string>(null);

    const [error, setError] = useState<ErrorDetailContainer | null>(null);
    const [requestInProgres, setRequestInProgress] = useState(false);

    const handleGenderChange = (_, v: Gender) => {
        if (v === null) {
            return;
        }

        setGender(v);
    };

    const handleFirstNameChange = (
        ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFirstName(ev.target.value);
    };

    const handleLastNameChange = (
        ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setLastName(ev.target.value);
    };

    const handleMobileNumberChange = (
        ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setMobileNumber(ev.target.value);
    };

    const handleJobTitleChange = (
        ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setJobTitle(ev.target.value);
    };

    const handleEmailChange = (
        ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setEmail(ev.target.value);
    };

    const handleAgeChange = (
        ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setAge(parseInt(ev.target.value));
    };

    const handlePinChange = (
        ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setPin(ev.target.value);
    };

    const handleFathersNameChange = (
        ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFathersName(ev.target.value);
    };

    const upsert = () => {
        if (props.employee) {
            updateEmployee();
        } else {
            createEmployee();
        }
    };

    const createEmployee = () => {
        setRequestInProgress(true);
        EmployeeService.createEmployee("1", {
            firstName,
            lastName,
            mobileNumber,
            jobTitle,
            email,
            age,
            gender,
            nationalCode: pin,
            fathersName,
        })
            .then((r) => {
                closeDialog(true);
            })
            .catch((e) => {
                setError(e);
            })
            .finally(() => {
                setRequestInProgress(false);
            });
    };

    const updateEmployee = () => {
        setRequestInProgress(true);
        EmployeeService.updateEmployee(props.employee.id, "1", {
            firstName,
            lastName,
            mobileNumber,
            jobTitle,
            email,
            age,
            gender,
            nationalCode: pin,
            fathersName,
        })
            .then((r) => {
                closeDialog(true);
            })
            .catch((e) => {
                setError(e);
            })
            .finally(() => {
                setRequestInProgress(false);
            });
    };

    return (
        <DialogContainer>
            <DialogHeader title="ایجاد/ویرایش کاربران" />

            <Stack spacing={2} sx={{ p: 2 }}>
                <ToggleButtonGroup
                    fullWidth
                    color="primary"
                    value={gender}
                    exclusive
                    onChange={handleGenderChange}
                >
                    <ToggleButton value={Gender.MALE}>آقا</ToggleButton>
                    <ToggleButton value={Gender.FEMALE}>خانم</ToggleButton>
                </ToggleButtonGroup>

                <TextField
                    label="نام"
                    value={firstName}
                    variant="outlined"
                    onChange={handleFirstNameChange}
                />

                <TextField
                    label="نام خانوادگی"
                    value={lastName}
                    variant="outlined"
                    onChange={handleLastNameChange}
                />

                <TextField
                    label="شماره موبایل"
                    value={mobileNumber}
                    variant="outlined"
                    onChange={handleMobileNumberChange}
                />

                <TextField
                    label="سمت"
                    value={jobTitle}
                    variant="outlined"
                    helperText="مثلا: سرپرست کارشناس فروش"
                    onChange={handleJobTitleChange}
                />

                <TextField
                    label="ایمیل"
                    value={email}
                    variant="outlined"
                    onChange={handleEmailChange}
                />

                <TextField
                    label="سن"
                    value={age}
                    type="number"
                    variant="outlined"
                    helperText="مثلا: 29"
                    onChange={handleAgeChange}
                />

                <Divider />

                <Alert severity="info">
                    مقادیر زیر برای دریافت گواهی اجباری است
                </Alert>

                <TextField
                    label="کد ملی"
                    required
                    value={pin}
                    variant="outlined"
                    onChange={handlePinChange}
                />

                <TextField
                    required
                    label="نام پدر"
                    value={fathersName}
                    variant="outlined"
                    onChange={handleFathersNameChange}
                />

                <ErrorParser error={error} />

                <Button
                    variant="contained"
                    disabled={requestInProgres}
                    onClick={upsert}
                >
                    اعمال تغییرات
                </Button>
            </Stack>
        </DialogContainer>
    );
}
